import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { throttle } from 'lodash'
import { View } from 'oio-react'
import PropTypes from 'prop-types'
import Helmet from 'react-helmet'
import noteStatuses from 'config/constants/noteStatuses'
import Title from 'src/apps/CustomApp/components/Title'
import NotebookContext from 'src/libraries/contexts/Notebook'
import ProtoThemeContext from 'src/libraries/contexts/ProtoTheme'
import { usePrevious } from 'src/components/utils'
import { useNote, useUpdateNote, useRequestNoteLock, useOnNoteUpdated } from 'src/graphql/hooks'
import MediaBlock from '~/components/MediaBlock'
import NoteMoreControl from '~/components/NoteMoreControl'
import NoteHeaderContainer from '~/components/NoteHeaderContainer'
import NoteLayoutContainer from '~/components/NoteLayoutContainer'
import NoteStatusBlock from '~/components/NoteStatusBlock'
import TextEditV2 from '~/components/TextV2'
import TextEditV3 from '~/components/TextV3'

const NoteEdit = ({ id: noteId, onRemove, onUpdate }) => {
   const { protoTheme: theme } = useContext(ProtoThemeContext)
   const { useFacingPages, visibleNoteIds } = useContext(NotebookContext)
   const [noteStatus, setNoteStatus] = useState(noteStatuses.NOTE_STATUS_READY)

   const { note, networkStatus, refetch } = useNote({ id: noteId })
   const { requestNoteLock } = useRequestNoteLock()
   const { updateNote } = useUpdateNote()

   const isMediaNote = note?.content?.elements?.[0]?.__typename === 'FileListElement'
   const isTextNote = !isMediaNote

   const notePrevNetworkStatus = usePrevious(networkStatus)
   const noteTitleInputRef = useRef(null)

   useEffect(() => {
      if (networkStatus <= 2) {
         setNoteStatus(noteStatuses.NOTE_STATUS_LOADING)
      } else if (note.locked && !note.lockedByCurrentConnection) {
         setNoteStatus(noteStatuses.NOTE_STATUS_LOCKED)
      } else if (note.locked && note.lockedByCurrentConnection) {
         setNoteStatus(noteStatuses.NOTE_STATUS_EDITING)
      } else {
         setNoteStatus(noteStatuses.NOTE_STATUS_READY)
      }
   }, [networkStatus, note])

   // Store the note's title in a state var so we can fully control the title input
   const [title, setTitle] = useState(note?.title)

   // Ensure we don't refetch constantly
   const throttledRefetch = useCallback(throttle(() => {
      refetch()
   }, 1000), [refetch])

   useOnNoteUpdated({ noteId }, {
      fetchPolicy: 'no-cache',
      onSubscriptionData: ({ subscriptionData }) => {
         const { lockedByCurrentConnection } = subscriptionData.data.noteUpdated
         if (!lockedByCurrentConnection && throttledRefetch) {
            throttledRefetch({ withContent: true })
         } else if (lockedByCurrentConnection) {
            setNoteStatus(noteStatuses.NOTE_STATUS_EDITING)
         }
      }
   })

   useEffect(() => {
      if (note) {
         if (!note.lockedByCurrentConnection || notePrevNetworkStatus <= 2) {
            setTitle(note.title)
         }
      }
   }, [note])

   const updateTitleThrottled = throttle(async () => {
      const newTitle = noteTitleInputRef.current.value
      await requestNoteLock({ id: note.id })
      updateNote({ id: note.id }, { title: newTitle }, { fetchPolicy: 'no-cache' })
   }, 3000)

   const handleTitleChanged = (e) => {
      updateTitleThrottled()
      setTitle(e.currentTarget.value)
   }

   if (networkStatus <= 2) {
      return null
   }

   return (
      <NoteLayoutContainer noteId={noteId}>
         {!useFacingPages && visibleNoteIds.includes(noteId) && (
            <Helmet title={`Note | ${note?.title}`} />
         )}
         <NoteHeaderContainer
            minHeight={theme.tmpViewMode !== 'sidebar' && !useFacingPages
               ? '18vh'
               : theme.tmpHeadingBlockHeight
            }>
            <View
               display="flex"
               alignItems="flex-start"
               justifyContent="space-between"
               width="100%">
               <View flex="1 1 auto">
                  <View
                     display="flex"
                     justifyContent="space-between"
                     width="100%"
                     marginBottom="40px">
                     <NoteStatusBlock
                        dateLastUpdated={note?.dateLastUpdated}
                        lastUpdatedByAuthor={note?.lastUpdatedBy?.fullName}
                        lockedBy={note?.lockedBy?.fullName}
                        lockedByCurrentConnection={note.lockedByCurrentConnection}
                        noteStatus={noteStatus}
                        textColor={theme.tmpNoteMetaTextColor}
                     />
                  </View>
                  <Title size="lg" color={theme.tmpContentTitleColor}>
                     <textarea
                        ref={noteTitleInputRef}
                        appearance="plain"
                        onChange={handleTitleChanged}
                        size="inherit"
                        name="title"
                        placeholder="Title"
                        value={title}
                        style={{
                           position: 'relative',
                           width: '100%',
                           left: '-2px',
                           padding: '0',
                           color: 'inherit',
                           backgroundColor: 'transparent',
                           fontFamily: 'inherit',
                           fontSize: 'inherit',
                           fontWeight: 'inherit',
                           letterSpacing: 'inherit',
                           lineHeight: 'inherit',
                           textTransform: 'inherit',
                           resize: 'none'
                        }}
                        rows="1"
                     />
                  </Title>
               </View>
               <View
                  display="flex"
                  flexFlow="row"
                  justifyContent="flex-end"
                  alignItems="center"
                  width="100%[a-c] 100px[d-f]">
                  <NoteMoreControl
                     noteId={note.id}
                     pinned={note.pinned}
                  />
               </View>
            </View>
         </NoteHeaderContainer>
         <View
            display="flex"
            justifyContent="center"
            width="100%"
            paddingHorizontal={theme.tmpNotePadding}>
            <View width="100%" maxWidth={theme.tmpNoteMaxWidth}>
               {isTextNote && note?.content?.v === 2 && (
                  <TextEditV2 note={note} noteStatus={noteStatus} />
               )}
               {isTextNote && note?.content?.v === 3 && (
                  <TextEditV3 note={note} noteStatus={noteStatus} />
               )}
               {isMediaNote && <MediaBlock note={note} />}
            </View>
         </View>
      </NoteLayoutContainer>
   )
}

NoteEdit.propTypes = {
   id: PropTypes.string.isRequired,
   onRemove: PropTypes.func.isRequired,
   onUpdate: PropTypes.func
}

NoteEdit.defaultProps = {
   onUpdate: undefined
}

export default NoteEdit
